import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "@phosphor-icons/react";
import { BookingDialog } from "./BookingDialog";
import { scrollToId } from "../hooks/useLenis";

const HERO_IMAGE = "https://images.unsplash.com/photo-1505664194779-8beaceb93744?auto=format&fit=crop&w=1800&q=80";
const PORTRAIT_IMAGE = "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=900&q=80";

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] overflow-hidden bg-[#0B0B0E]"
    >
      {/* Backdrop image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-55"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0E] via-[#0B0B0E]/85 to-[#0B0B0E]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0E] via-transparent to-[#0B0B0E]/70" />
      {/* Ambient gold pool */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 80% 30%, rgba(233,199,106,0.16), transparent 60%)",
        }}
      />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pt-40 md:pb-28 min-h-[100vh] flex flex-col justify-center">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left column: headline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-12 bg-[#E9C76A]" />
              <span className="overline">Solicitors  ·  Southall, London</span>
            </div>

            <h1 className="font-serif text-[40px] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#F5F2E8]">
              Considered counsel
              <br />
              for life&apos;s most{" "}
              <span className="italic gold-gradient-text" style={{ textShadow: "0 0 80px rgba(233,199,106,0.25)" }}>
                consequential
              </span>
              <br />
              decisions.
            </h1>

            <p className="mt-8 max-w-xl text-base md:text-lg text-white/70 font-light leading-relaxed">
              Lawfields is a niche London firm offering a personal, partner-led
              service across immigration, family, property, crime and civil
              litigation. We listen first — then act decisively.
            </p>

            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <BookingDialog
                trigger={
                  <button data-testid="hero-cta-book" className="btn-gold inline-flex items-center gap-3">
                    Book a Walk-In Slot
                    <ArrowDown size={14} weight="bold" className="-rotate-45" />
                  </button>
                }
              />
              <button
                data-testid="hero-cta-consult"
                onClick={() => scrollToId("contact")}
                className="btn-ghost-gold"
              >
                Send an Enquiry
              </button>
            </div>

            {/* Inline stats */}
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-7">
              <div data-testid="hero-stat-areas">
                <div className="font-serif text-3xl gold-gradient-text">5</div>
                <div className="overline mt-1 !text-white/45">Practice Areas</div>
              </div>
              <div data-testid="hero-stat-years">
                <div className="font-serif text-3xl gold-gradient-text">15+</div>
                <div className="overline mt-1 !text-white/45">Years</div>
              </div>
              <div data-testid="hero-stat-sra">
                <div className="font-serif text-3xl gold-gradient-text">SRA</div>
                <div className="overline mt-1 !text-white/45">Regulated</div>
              </div>
            </div>
          </motion.div>

          {/* Right column: photographic frame */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: "easeOut" }}
            className="lg:col-span-5 relative hidden lg:block"
          >
            <div
              className="relative aspect-[4/5] overflow-hidden"
              style={{
                boxShadow:
                  "inset 0 0 0 1px rgba(233,199,106,0.20), 0 30px 70px -20px rgba(0,0,0,0.7), 0 0 80px rgba(233,199,106,0.12)",
              }}
            >
              <img src={PORTRAIT_IMAGE} alt="Scales of justice" className="w-full h-full object-cover" />
              {/* Diagonal sheen */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 40%)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0E]/95 via-[#0B0B0E]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <div className="overline mb-3">Our Promise</div>
                <div className="font-serif text-xl text-[#F5F2E8] leading-snug">
                  Partner-led from first call
                  <br />
                  to final outcome.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.button
        onClick={() => scrollToId("about")}
        data-testid="hero-scroll-cue"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-white/50 hover:text-[#E9C76A] transition-colors"
        aria-label="Scroll down"
      >
        <span className="h-px w-8 bg-white/40" />
        Discover
        <ArrowDown size={12} weight="thin" />
      </motion.button>
    </section>
  );
};
